/**
 * Team Session Status Dashboard
 * Shows real-time status of all team members
 * For Team Leaders and Admins only
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { dedupedGet } from '../../lib/requestDedupe';
import { usePoller } from '../../hooks/usePoller';
import {
  Users,
  Activity,
  Clock,
  AlertCircle,
  RefreshCw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Settings,
  Loader2,
  ZapOff,
  Info,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';
import { formatDateTimeShortET } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    color: 'bg-green-500',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
    icon: Activity
  },
  idle: {
    label: 'Idle',
    color: 'bg-yellow-500',
    badgeClass: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Clock
  },
  inactive: {
    label: 'Inactive',
    color: 'bg-red-500',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertCircle
  },
  on_break: {
    label: 'On Break',
    color: 'bg-orange-500',
    badgeClass: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: Clock
  },
  it_issue: {
    label: 'Downtime',
    color: 'bg-amber-500',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: ZapOff
  }
};

const TeamSessionStatus = ({ currentUser }) => {
  const [teamStatus, setTeamStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [config, setConfig] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [promptHistory, setPromptHistory] = useState({});

  const isAdmin = currentUser?.role === 'admin';

  const fetchTeamStatus = useCallback(async (signal) => {
    try {
      const response = await dedupedGet(`${API}/api/session-health/team-status`, { signal });
      setTeamStatus(response.data);
    } catch (err) {
      console.error('Error fetching team status:', err);
      if (err.response?.status !== 403) {
        toast.error('Failed to load team status');
      }
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/session-health/config`);
      setConfig(response.data.config);
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, []);

  const fetchUserPromptHistory = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API}/api/session-health/prompt-history?user_id=${userId}&limit=5`);
      setPromptHistory(prev => ({
        ...prev,
        [userId]: response.data.prompts
      }));
    } catch (err) {
      console.error('Error fetching prompt history:', err);
    }
  }, []);

  useEffect(() => {
    fetchTeamStatus();
    fetchConfig();
  }, [fetchTeamStatus, fetchConfig]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware auto-refresh.
  usePoller(fetchTeamStatus, 30000, { immediate: false });

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTeamStatus();
  };

  const handleExpandUser = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      if (!promptHistory[userId]) {
        fetchUserPromptHistory(userId);
      }
    }
  };

  const handleSaveConfig = async (newConfig) => {
    try {
      await axios.put(`${API}/api/session-health/config`, newConfig);
      toast.success('Configuration saved');
      setShowConfigModal(false);
      setConfig(prev => ({ ...prev, ...newConfig }));
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error('Failed to save configuration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!teamStatus) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">Unable to load team status</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Team Session Status</h2>
          <p className="text-sm text-gray-500 mt-1">
            Real-time activity monitoring for clocked-in team members
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowConfigModal(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-600">Total Online</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{teamStatus.summary.total}</div>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-600">Active</span>
          </div>
          <div className="text-2xl font-bold text-green-700">{teamStatus.summary.active}</div>
        </div>
        <div className="p-4 bg-yellow-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-600">Idle</span>
          </div>
          <div className="text-2xl font-bold text-yellow-700">{teamStatus.summary.idle}</div>
        </div>
        <div className="p-4 bg-red-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm text-red-600">Inactive</span>
          </div>
          <div className="text-2xl font-bold text-red-700">{teamStatus.summary.inactive}</div>
        </div>
      </div>

      {/* Threshold Info */}
      {config && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-4 py-2">
          <span className="font-medium">Thresholds:</span>{' '}
          Active (0-{config.active_threshold_minutes} min) | 
          Idle ({config.active_threshold_minutes}-{config.idle_threshold_minutes} min) | 
          Inactive ({config.idle_threshold_minutes}+ min)
        </div>
      )}

      {/* Team Members List */}
      {teamStatus.team_members.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No team members currently clocked in</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Last Activity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Clock In</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-1 cursor-help">
                          Prompts Today
                          <Info className="w-3 h-3 text-gray-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Number of work proof prompts shown today</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {teamStatus.team_members.map((member) => {
                const statusConfig = STATUS_CONFIG[member.status] || STATUS_CONFIG.inactive;
                const StatusIcon = statusConfig.icon;
                
                // Determine if this user needs attention (Inactive + Clocked in + No prompt response today)
                const needsAttention = member.status === 'inactive' && 
                                       member.clock_in && 
                                       member.prompts_today === 0;
                
                // Get activity display text
                const getActivityText = () => {
                  if (!member.clock_in) {
                    return 'Not clocked in';
                  }
                  if (member.minutes_since_activity === null) {
                    return 'Clocked in – No activity';
                  }
                  return `${Math.round(member.minutes_since_activity)} min ago`;
                };
                
                // Get clock-in display text
                const getClockInText = () => {
                  if (!member.clock_in) {
                    return 'Not clocked in';
                  }
                  // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local time-only
                  return new Date(member.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                };
                
                return (
                  <React.Fragment key={member.user_id}>
                    <tr className={`hover:bg-gray-50 ${needsAttention ? 'bg-orange-50/50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {needsAttention && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Needs attention: Inactive with no prompt response</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{member.user_name}</p>
                            <p className="text-xs text-gray-500">{member.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${statusConfig.badgeClass} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${!member.clock_in ? 'text-gray-400 italic' : 'text-gray-600'}`}>
                          {getActivityText()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${!member.clock_in ? 'text-gray-400 italic' : 'text-gray-500'}`}>
                          {getClockInText()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">{member.prompts_today}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExpandUser(member.user_id)}
                        >
                          {expandedUser === member.user_id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedUser === member.user_id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-gray-50">
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Recent Work Proof Responses
                            </h4>
                            {promptHistory[member.user_id]?.length > 0 ? (
                              <div className="space-y-2">
                                {promptHistory[member.user_id].map((prompt) => (
                                  <div 
                                    key={prompt.id} 
                                    className="p-3 bg-white border border-gray-200 rounded-lg"
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <p className="text-sm text-gray-800">
                                          {prompt.response_text || <em className="text-gray-400">Awaiting response...</em>}
                                        </p>
                                        {prompt.linked_order_id && prompt.linked_order_id !== 'none' && (
                                          <p className="text-xs text-blue-600 mt-1">
                                            Linked to order: {prompt.linked_order_id}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right text-xs text-gray-500">
                                        <p>{formatDateTimeShortET(prompt.triggered_at)}</p>
                                        {prompt.response_time_seconds && (
                                          <p>Response: {Math.round(prompt.response_time_seconds / 60)} min</p>
                                        )}
                                        {prompt.acknowledged && (
                                          <Badge variant="outline" className="mt-1 text-green-600 border-green-200">
                                            Acknowledged
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">No work proof responses recorded today.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Config Modal (Admin Only) */}
      <ConfigModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        config={config}
        onSave={handleSaveConfig}
      />
    </div>
  );
};

// Configuration Modal Component
const ConfigModal = ({ open, onClose, config, onSave }) => {
  const [formData, setFormData] = useState({
    active_threshold_minutes: 7,
    idle_threshold_minutes: 20,
    max_prompts_per_day: 2,
    prompt_cooldown_minutes: 15,
    recent_activity_buffer_minutes: 5
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setFormData({
        active_threshold_minutes: config.active_threshold_minutes || 7,
        idle_threshold_minutes: config.idle_threshold_minutes || 20,
        max_prompts_per_day: config.max_prompts_per_day || 2,
        prompt_cooldown_minutes: config.prompt_cooldown_minutes || 15,
        recent_activity_buffer_minutes: config.recent_activity_buffer_minutes || 5
      });
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Session Health Settings</DialogTitle>
          <DialogDescription>
            Configure activity thresholds and work proof prompt settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Active Threshold (min)
              </label>
              <Input
                type="number"
                min={1}
                max={30}
                value={formData.active_threshold_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, active_threshold_minutes: parseInt(e.target.value) || 7 }))}
              />
              <p className="text-xs text-gray-500 mt-1">0 to this = Active</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Idle Threshold (min)
              </label>
              <Input
                type="number"
                min={5}
                max={60}
                value={formData.idle_threshold_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, idle_threshold_minutes: parseInt(e.target.value) || 20 }))}
              />
              <p className="text-xs text-gray-500 mt-1">After this = Inactive</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Max Prompts Per Day
            </label>
            <Input
              type="number"
              min={0}
              max={10}
              value={formData.max_prompts_per_day}
              onChange={(e) => setFormData(prev => ({ ...prev, max_prompts_per_day: parseInt(e.target.value) || 2 }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Login Cooldown (min)
              </label>
              <Input
                type="number"
                min={5}
                max={60}
                value={formData.prompt_cooldown_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt_cooldown_minutes: parseInt(e.target.value) || 15 }))}
              />
              <p className="text-xs text-gray-500 mt-1">No prompts after login</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Activity Buffer (min)
              </label>
              <Input
                type="number"
                min={1}
                max={15}
                value={formData.recent_activity_buffer_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, recent_activity_buffer_minutes: parseInt(e.target.value) || 5 }))}
              />
              <p className="text-xs text-gray-500 mt-1">Skip if recent activity</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamSessionStatus;
